import posts from '@/lib/blog-posts.json';
import calendarPng from '@/assets/harbor_ss_calendar.png';
import chatPng from '@/assets/harbor_ss_chat.png';
import expenseReceiptPng from '@/assets/harbor_ss_expense_receipt.png';
import exportsPng from '@/assets/harbor_ss_exports.png';
import vaultPng from '@/assets/harbor_ss_file_vault.png';
import journalPng from '@/assets/harbor_ss_journal.png';
import chatThreadPng from '@/assets/harbor_screen_chat_thread.png';
import expensesBalancePng from '@/assets/harbor_screen_expenses_balance.png';
import temperatureCheckPng from '@/assets/harbor_screen_temperature_check.png';

const BLOG_IMAGES = {
  calendar: calendarPng,
  chat: chatPng,
  expenseReceipt: expenseReceiptPng,
  exports: exportsPng,
  vault: vaultPng,
  journal: journalPng,
  chatThread: chatThreadPng,
  expensesBalance: expensesBalancePng,
  temperatureCheck: temperatureCheckPng,
} as const;

export type BlogImageKey = keyof typeof BLOG_IMAGES;

// Raw app captures have no device bezel, so the page draws a phone-shaped frame.
const BARE_SCREEN_IMAGES = new Set<BlogImageKey>([
  'chatThread',
  'expensesBalance',
  'temperatureCheck',
]);

export type BlogImage = { src: string; bareScreen: boolean };

export type BlogSection = {
  heading: string;
  paragraphs: string[];
  bullets?: string[];
};

export type BlogLink = {
  href: string;
  label: string;
  description: string;
};

export type BlogPost = {
  slug: string;
  title: string;
  seoTitle: string;
  description: string;
  publishedAt: string;
  updatedAt: string;
  image?: string;
  imageAlt?: string;
  excerpt: string;
  sections: BlogSection[];
  related: BlogLink[];
};

export const BLOG_POSTS: BlogPost[] = [...(posts as BlogPost[])].sort((a, b) =>
  b.publishedAt.localeCompare(a.publishedAt)
);

export const BLOG_POSTS_BY_SLUG: Record<string, BlogPost> = Object.fromEntries(
  BLOG_POSTS.map((post) => [post.slug, post])
);

export function blogPostPath(slug: string): string {
  return `/blog/${slug}`;
}

export function getBlogPost(slug: string): BlogPost | undefined {
  return BLOG_POSTS_BY_SLUG[slug];
}

export function getBlogImage(post: BlogPost): BlogImage | undefined {
  if (!post.image) return undefined;
  const key = post.image as BlogImageKey;
  const src = BLOG_IMAGES[key];
  if (!src) return undefined;
  return { src, bareScreen: BARE_SCREEN_IMAGES.has(key) };
}

export function blogImageFrameClass(image: BlogImage): string {
  return image.bareScreen
    ? 'rounded-[2rem] border-[6px] border-gray-900 shadow-xl'
    : '';
}

export function formatBlogDate(isoDate: string): string {
  const [year, month, day] = isoDate.split('-').map(Number);
  if (!year || !month || !day) return isoDate;
  return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}
